import { userAuthContextType } from '../../auth/AuthProvider';
import { axios, settings, getResponse } from '../../helpers/main';

const { backend } = settings;
const authServices = { user: userAuthContextType }; //eslint-disable-line
const objectService = getResponse({ type: 0 }); //eslint-disable-line
/**
 * Service to manage communication with the Status API.
 * Follows the Single Responsibility Principle (SRP) by separating network logic from the UI.
 */
export const StatusService = {
    /**
     * Retrieves all Statuses.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    getAll: async ({
        auth,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.status}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                method: backend?.methods?.status?.getAll,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Retrieves a Status by ID.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number|string} params.id - Status ID.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    getById: async ({
        auth,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.status}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                id: id,
                languageId: languageId,
                method: backend?.methods?.status?.getById,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Creates a new Status.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {Object} params.data - Status data to create.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    create: async ({
        auth,
        data,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.status}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: data,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Updates an existing Status.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number|string} params.id - Status ID.
     * @param {Object} params.data - Status data to update.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    update: async ({
        auth,
        id,
        data,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.status}/${id}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: data,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Deletes a Status by ID.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number|string} params.id - Status ID to delete.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    deleteAsync: async ({
        auth,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.status}/${id}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.delete(requestObj);
    },
};
